import { configureStore } from '@reduxjs/toolkit'
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  Persistor,
  persistStore as doPersistStore,
} from 'redux-persist'
import {
  reducer as currentSaleReducer,
  setCurrentSale,
} from './slices/currentSale/currentSaleSlice'
import { reducer as posStateReducer } from './slices/posState/posStateSlice'
import { reducer as uiStateReducer } from './slices/uiStateSlice'

export const store = configureStore({
  reducer: {
    currentSale: currentSaleReducer,
    posState: posStateReducer,
    uiState: uiStateReducer,
  },
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          // Ignoring redux-persist actions as per https://redux-toolkit.js.org/usage/usage-guide#use-with-redux-persist
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          // We pass a full Sale model to this action which is not serializable (has prototype methods). This is
          // intentional and necessary until the migration to Redux is complete. We don't save the entire Sale, only
          // cherry pick the state we care about (which is all serializable).
          setCurrentSale.type,
        ],
      },
    }),
})

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

let resolveBootstrap: () => void
const whenBootstrapped = new Promise<void>(resolve => {
  resolveBootstrap = resolve
})

let persistor: Persistor

/**
 * Enable persistence of the Redux store using redux-persist. Reducers configured to persist their state will
 * load state from and persist state to the appropriate persistence layer (e.g. posStateSlice to 'settings'
 * IndexedDB store).
 *
 * @returns {Promise} A promise resolved once the initial state has been loaded from the persistence layer(s)
 */
export function persistStore() {
  if (!persistor) {
    persistor = doPersistStore(store)

    const unsubscribe = persistor.subscribe(() => {
      const { bootstrapped } = persistor.getState()
      if (bootstrapped) {
        unsubscribe()
        resolveBootstrap()
      }
    })
  }

  return whenBootstrapped
}

/**
 * Wait for initial state to be loaded into the Redux store from the persistence layer(s).
 *
 * @returns {Promise} A promise resolved once the initial state has been loaded from the persistence layer(s)
 */
export function whenStoreBootstrapped(): Promise<void> {
  return whenBootstrapped
}
